import {
  type EpocusEncounter,
  RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
  useRxCollectionEpocusEncounter,
} from "$global-providers/rxdb-provider/collections/epocus-encounter";
import {
  type EpocusExam,
  RXDB_EPOCUS_EXAM_COLLECTION_NAME,
  useRxCollectionEpocusExam,
} from "$global-providers/rxdb-provider/collections/epocus-exam";
import { useRxCollectionEpocusReview } from "$global-providers/rxdb-provider/collections/epocus-review";
import {
  isServerTimeUnknownError,
  serverTimeUnknownMessage,
  useServerTime,
} from "$global-providers/server-time-provider/provider";
import { toast } from "$global-providers/toast-provider";
import { useUser } from "$global-providers/user-provider";
import { randomUniqueId } from "$routes/_lib/random-id";
import {
  Box,
  Button,
  CircularProgress,
  styled,
  TextField,
  Typography,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ScheduleIcon from "@material-ui/icons/Schedule";
import { useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { useRxData } from "rxdb-hooks";
import { useBillingCodesEnabled } from "../../_lib/billing/use-billing-codes-enabled";
import { formatDicomPatientName } from "../../_lib/dicom-patient-name";
import { DEVICE_LABELS, EXAM_LABELS } from "../../_lib/epocus-exam-constants";
import {
  EPOCUS_BG,
  EPOCUS_BORDER,
  EPOCUS_PRIMARY,
  EPOCUS_SUCCESS,
  EPOCUS_SUCCESS_DARK,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "../../_lib/epocus-palette";
import { EpocusStarRating } from "../../_lib/epocus-star-rating";
import { epocusTheme } from "../../_lib/epocus-theme-provider";
import { useEpocusExamImages } from "../../_lib/hooks/use-epocus-exam-images";
import { usePerformerName } from "../../_lib/hooks/use-performer-name";
import { ImageQualityDefinitions } from "../../_lib/image-quality-definitions";
import { SCORE_LOW } from "../../_lib/qa-score-colors";
import { RoleGate } from "../../_lib/rbac/role-gate";
import {
  isEscalating,
  isSelectableAgreement,
  type SelectableAgreement,
} from "../../_lib/review-agreement";
import { ReviewOutcomeCard } from "../../_lib/review-outcome-card";
import { useActiveProgram } from "../../_lib/use-active-program";
import { useExamReview } from "../../_lib/use-exam-review";
import {
  mskImageGroupLabel,
  sortImagesByMskRegion,
} from "../../_lib/worksheets/msk-regions";
import { linkToEpocusReview } from "../../links";
import { AgreementControl } from "./_lib/agreement-control";
import { FeedbackTagPicker } from "./_lib/feedback-tag-picker";
import { RescoreReview } from "./_lib/rescore-review";
import { ReviewScanPane } from "./_lib/review-scan-pane";
import {
  buildEncounterSignOffPatch,
  buildUnsignedReview,
  everyExamReviewed,
  signReview,
} from "./_lib/review-submission";
import { describeTurnaround } from "./_lib/review-target-clock";
import { SignedReviewPanel } from "./_lib/signed-review-panel";
import { useNextInQueue } from "./_lib/use-next-in-queue";
import { completedViewLabels } from "./_lib/worksheet-summary";
import { WorksheetSummaryCard } from "./_lib/worksheet-summary-card";

const CLOCK_TICK_MS = 30_000;

const PageRoot = styled(Box)({
  display: "flex",
  flexDirection: "column",
  minHeight: "100%",
  backgroundColor: EPOCUS_BG,
});

const Header = styled(Box)({
  backgroundColor: EPOCUS_WHITE,
  borderBottom: `1px solid ${EPOCUS_BORDER}`,
  padding: "12px 16px",
  display: "flex",
  alignItems: "center",
  gap: 12,
  [epocusTheme.breakpoints.up("sm")]: {
    padding: "14px 24px",
  },
});

const BackButton = styled("button")({
  width: 44,
  height: 44,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "none",
  border: "none",
  cursor: "pointer",
  borderRadius: 10,
  color: EPOCUS_PRIMARY,
  flexShrink: 0,
  "&:hover": { backgroundColor: EPOCUS_BG },
  "&:focus-visible": {
    outline: `2px solid ${EPOCUS_PRIMARY}`,
    outlineOffset: 2,
  },
});

const HeaderTitle = styled(Typography)({
  fontSize: rem(16),
  fontWeight: 700,
  color: EPOCUS_TEXT_PRIMARY,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const HeaderSubtitle = styled(Typography)({
  fontSize: rem(13),
  color: EPOCUS_TEXT_SECONDARY,
  fontVariantNumeric: "tabular-nums",
});

const SplitLayout = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  [epocusTheme.breakpoints.up("sm")]: {
    flexDirection: "row",
    alignItems: "stretch",
  },
});

const ScanColumn = styled(Box)({
  height: "38vh",
  minHeight: 260,
  [epocusTheme.breakpoints.up("sm")]: {
    flex: "0 0 50%",
    maxWidth: "50%",
    height: "auto",
    position: "sticky",
    top: 0,
    maxHeight: "calc(100dvh - 72px)",
  },
});

const WorkflowColumn = styled(Box)({
  display: "flex",
  flexDirection: "column",
  backgroundColor: EPOCUS_WHITE,
  [epocusTheme.breakpoints.up("sm")]: {
    flex: "0 0 50%",
    maxWidth: "50%",
    borderLeft: `1px solid ${EPOCUS_BORDER}`,
    maxHeight: "calc(100dvh - 72px)",
  },
});

const WorkflowBody = styled(Box)({
  flex: 1,
  padding: "16px 20px",
  display: "flex",
  flexDirection: "column",
  gap: 20,
  [epocusTheme.breakpoints.up("sm")]: {
    overflowY: "auto",
  },
});

const WorkflowFooter = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 11,
  padding: "14px 20px",
  borderTop: `1px solid ${EPOCUS_BORDER}`,
  backgroundColor: EPOCUS_WHITE,
});

const TargetClock = styled(Typography)({
  marginRight: "auto",
  display: "flex",
  alignItems: "center",
  gap: 6,
  fontSize: rem(12.5),
  color: EPOCUS_TEXT_SECONDARY,
});

const SubmitButton = styled(Button)({
  minHeight: 48,
  borderRadius: 10,
  fontSize: rem(15),
  fontWeight: 700,
  textTransform: "none",
  backgroundColor: EPOCUS_SUCCESS,
  color: EPOCUS_WHITE,
  "&:hover": { backgroundColor: EPOCUS_SUCCESS_DARK },
  "&:disabled": {
    backgroundColor: EPOCUS_BORDER,
    color: EPOCUS_TEXT_SECONDARY,
  },
});

const SaveDraftButton = styled(Button)({
  minHeight: 48,
  borderRadius: 10,
  fontSize: rem(15),
  fontWeight: 700,
  textTransform: "none",
  borderColor: EPOCUS_PRIMARY,
  color: EPOCUS_PRIMARY,
});

const BackToQueueButton = styled(Button)({
  marginLeft: "auto",
  minHeight: 48,
  borderRadius: 10,
  textTransform: "none",
  borderColor: EPOCUS_PRIMARY,
  color: EPOCUS_PRIMARY,
});

const SectionLabel = styled(Typography)({
  fontSize: rem(12),
  fontWeight: 700,
  letterSpacing: "0.02em",
  color: EPOCUS_TEXT_SECONDARY,
  textTransform: "uppercase",
  marginBottom: 10,
});

const OptionalHint = styled("span")({
  fontWeight: 400,
  textTransform: "none",
  letterSpacing: 0,
});

const Section: FCwC<{ label: string; hint?: string }> = ({
  label,
  hint,
  children,
}) => (
  <Box>
    <SectionLabel>
      {label} {hint && <OptionalHint>{hint}</OptionalHint>}
    </SectionLabel>
    {children}
  </Box>
);

export const EpocusReviewPanelPage: FC = () => {
  const { examId } = useParams<{ examId: string }>();
  const navigate = useNavigate();
  const user = useUser();
  const { formatMessage } = useIntl();
  const { getCurrentTime, getDisplayTime } = useServerTime();

  const [agreement, setAgreement] = useState<SelectableAgreement | null>(null);
  const [imageQualityScore, setImageQualityScore] = useState(0);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [comment, setComment] = useState("");
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [justSigned, setJustSigned] = useState(false);
  const [encounterComplete, setEncounterComplete] = useState(false);
  const [now, setNow] = useState(() => getDisplayTime());
  const reviewId = useRef(randomUniqueId());
  const draftCreatedAt = useRef<string | undefined>(undefined);
  const hydratedDraftId = useRef<string | null>(null);

  const billingCodesEnabled = useBillingCodesEnabled();
  const [rescoring, setRescoring] = useState(false);

  const reviewCollection = useRxCollectionEpocusReview();
  const encounterCollection = useRxCollectionEpocusEncounter();
  const examCollection = useRxCollectionEpocusExam();

  useEffect(() => {
    const timer = window.setInterval(
      () => setNow(getDisplayTime()),
      CLOCK_TICK_MS,
    );
    return () => window.clearInterval(timer);
  }, [getDisplayTime]);

  const { result: exams, isFetching: examFetching } = useRxData<EpocusExam>(
    RXDB_EPOCUS_EXAM_COLLECTION_NAME,
    (collection) =>
      examId
        ? collection.find({ selector: { id: { $eq: examId } } })
        : undefined,
  );

  const exam = exams[0];

  const { result: encounters } = useRxData<EpocusEncounter>(
    RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
    (collection) =>
      exam
        ? collection.find({ selector: { id: { $eq: exam.encounterId } } })
        : undefined,
  );

  const encounter = encounters[0];

  const orderedImages = useMemo(
    () =>
      exam?.examType === "msk"
        ? sortImagesByMskRegion(exam.images)
        : (exam?.images ?? []),
    [exam],
  );
  const hydratedImages = useEpocusExamImages(orderedImages);
  const { review, draft } = useExamReview(examId, user.id);
  const program = useActiveProgram();
  const nextInQueue = useNextInQueue(encounter?.id ?? null);
  const { displayName: performerName } = usePerformerName(
    encounter?.performerId,
  );

  useEffect(() => {
    if (!draft || hydratedDraftId.current === draft.id) return;
    hydratedDraftId.current = draft.id;
    reviewId.current = draft.id;
    draftCreatedAt.current = draft.meta.createdAt;
    setAgreement(
      isSelectableAgreement(draft.agreement) ? draft.agreement : null,
    );
    setImageQualityScore(draft.imageQualityScore ?? draft.qualityScore);
    setSelectedTags([...draft.feedbackTags]);
    setComment(draft.comment ?? "");
  }, [draft]);

  const examLabel = exam
    ? (EXAM_LABELS[exam.examType] ?? exam.examType)
    : "Exam";

  const viewsLabel = useMemo(() => {
    if (!exam) return examLabel;
    const views = completedViewLabels(exam.worksheetData);
    return views.length > 0 ? `${examLabel} · ${views.join(" / ")}` : examLabel;
  }, [exam, examLabel]);

  const turnaround = describeTurnaround({
    startedAt: encounter?.assignedAt ?? encounter?.submittedForReviewAt ?? null,
    targetMinutes: program?.turnaroundTargetMinutes ?? null,
    now,
  });

  const locked = review?.signedAt != null;
  const isOwnStudy = encounter?.performerId === user.id;
  const canWriteReview =
    agreement !== null &&
    imageQualityScore >= 1 &&
    !submitting &&
    !saving &&
    !locked &&
    !isOwnStudy;

  const performerLabel = [
    performerName,
    encounter?.deviceId
      ? (DEVICE_LABELS[encounter.deviceId] ?? encounter.deviceId)
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const goToNext = () => {
    navigate(nextInQueue ? nextInQueue.link : linkToEpocusReview());
  };

  const buildFromForm = (
    at: string,
    examDoc: EpocusExam,
    reviewAgreement: SelectableAgreement,
  ) =>
    buildUnsignedReview({
      id: reviewId.current,
      exam: examDoc,
      reviewerId: user.id,
      agreement: reviewAgreement,
      imageQualityScore,
      feedbackTags: selectedTags,
      comment,
      now: at,
      createdAt: draftCreatedAt.current,
    });

  const handleSave = async () => {
    if (!canWriteReview || !exam || !agreement || !reviewCollection) return;

    setSaving(true);
    try {
      const savedAt = getCurrentTime().toISOString();
      const unsigned = buildFromForm(savedAt, exam, agreement);
      draftCreatedAt.current = unsigned.meta.createdAt;
      hydratedDraftId.current = unsigned.id;
      await reviewCollection.upsert(unsigned);
      toast.success("Draft saved.");
    } catch (err) {
      toast.error(
        isServerTimeUnknownError(err)
          ? formatMessage(serverTimeUnknownMessage)
          : "Failed to save the draft. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!canWriteReview || !exam || !agreement || !reviewCollection) return;

    setSubmitting(true);
    try {
      const signedAt = getCurrentTime().toISOString();
      await reviewCollection.upsert(
        signReview(buildFromForm(signedAt, exam, agreement), signedAt),
      );

      const encounterDoc = await encounterCollection
        ?.findOne({ selector: { id: { $eq: exam.encounterId } } })
        .exec();
      const siblingExams =
        (await examCollection
          ?.find({ selector: { encounterId: { $eq: exam.encounterId } } })
          .exec()) ?? [];
      const siblingReviews = await reviewCollection
        .find({ selector: { examId: { $in: siblingExams.map((e) => e.id) } } })
        .exec();
      const justSignedReview = { examId: exam.id, signedAt };

      const allExamsReviewed = everyExamReviewed({
        exams: siblingExams.map(({ id, status }) => ({ id, status })),
        reviews: [
          justSignedReview,
          ...siblingReviews.map(({ examId, signedAt }) => ({
            examId,
            signedAt,
          })),
        ],
      });

      if (encounterDoc) {
        await encounterDoc.incrementalModify((data) => ({
          ...data,
          ...buildEncounterSignOffPatch({
            encounter: data,
            agreement,
            everyExamReviewed: allExamsReviewed,
            now: signedAt,
          }),
          meta: {
            ...data.meta,
            updatedAt: signedAt,
            updatedByUserId: user.id,
          },
        }));
      }

      setEncounterComplete(allExamsReviewed);
      setJustSigned(true);
    } catch (err) {
      toast.error(
        isServerTimeUnknownError(err)
          ? formatMessage(serverTimeUnknownMessage)
          : "Failed to sign the review. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleRescore = async (score: number) => {
    if (!review || !reviewCollection) return;
    setRescoring(true);
    try {
      const doc = await reviewCollection.findOne(review.id).exec();
      if (!doc) return;
      const at = getCurrentTime().toISOString();
      await doc.incrementalModify((data) => ({
        ...data,
        imageQualityScore: score,
        qualityScore: score,
        meta: { ...data.meta, updatedAt: at, updatedByUserId: user.id },
      }));
      toast.success("Score updated.");
    } catch (err) {
      toast.error(
        isServerTimeUnknownError(err)
          ? formatMessage(serverTimeUnknownMessage)
          : "Failed to update the score. Please try again.",
      );
    } finally {
      setRescoring(false);
    }
  };

  if (examFetching || !exam) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={200}
      >
        <CircularProgress size={32} style={{ color: EPOCUS_PRIMARY }} />
      </Box>
    );
  }

  const patientLabel = `${formatDicomPatientName(encounter?.patientName) || "Patient"} — ${examLabel}`;

  const header = (
    <Header>
      <BackButton
        onClick={() => navigate(linkToEpocusReview())}
        aria-label="Back to review queue"
      >
        <ArrowBackIcon />
      </BackButton>
      <Box flex={1} minWidth={0}>
        <HeaderTitle>{patientLabel}</HeaderTitle>
        {encounter?.accessionNumber && (
          <HeaderSubtitle>{encounter.accessionNumber}</HeaderSubtitle>
        )}
      </Box>
    </Header>
  );

  if (justSigned) {
    return (
      <PageRoot>
        {header}
        <SignedReviewPanel
          escalated={agreement !== null && isEscalating(agreement)}
          encounterComplete={encounterComplete}
          onNext={goToNext}
          nextLabel={nextInQueue ? "Next in queue →" : "Back to queue"}
        />
      </PageRoot>
    );
  }

  return (
    <PageRoot>
      {header}
      <SplitLayout>
        <ScanColumn>
          <ReviewScanPane
            images={hydratedImages}
            selectedIndex={selectedImageIndex}
            onSelect={setSelectedImageIndex}
            viewsLabel={viewsLabel}
            groupLabelOf={
              exam?.examType === "msk" ? mskImageGroupLabel : undefined
            }
          />
        </ScanColumn>

        <WorkflowColumn>
          <WorkflowBody>
            <Section label="Worksheet summary">
              <WorksheetSummaryCard
                exam={exam}
                performerLabel={performerLabel}
              />
            </Section>

            {locked && review ? (
              <>
                <ReviewOutcomeCard review={review} />
                {billingCodesEnabled && review.reviewerId === user.id && (
                  <Section label="Image quality">
                    <RescoreReview
                      key={review.meta.updatedAt}
                      review={review}
                      saving={rescoring}
                      onSave={handleRescore}
                    />
                  </Section>
                )}
              </>
            ) : isOwnStudy ? (
              <Typography
                style={{ fontSize: rem(14), color: EPOCUS_TEXT_SECONDARY }}
              >
                You performed this study, so another reviewer needs to review
                it.
              </Typography>
            ) : (
              <>
                <Section label="Agree with findings?">
                  <AgreementControl value={agreement} onChange={setAgreement} />
                </Section>

                <Section label="Image quality">
                  <EpocusStarRating
                    value={imageQualityScore}
                    onChange={setImageQualityScore}
                    wholeStarsOnly
                  />
                  {billingCodesEnabled && (
                    <ImageQualityDefinitions value={imageQualityScore} />
                  )}
                </Section>

                <Section label="Feedback tags" hint="(optional)">
                  <FeedbackTagPicker
                    selected={selectedTags}
                    onToggle={(tag) =>
                      setSelectedTags((tags) =>
                        tags.includes(tag)
                          ? tags.filter((t) => t !== tag)
                          : [...tags, tag],
                      )
                    }
                  />
                </Section>

                <Section label="Comment" hint="(optional)">
                  <TextField
                    multiline
                    minRows={3}
                    maxRows={6}
                    fullWidth
                    variant="outlined"
                    placeholder="Add any additional feedback…"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    InputProps={{
                      style: { fontSize: rem(14), borderRadius: 8 },
                    }}
                  />
                </Section>
              </>
            )}
          </WorkflowBody>

          <WorkflowFooter>
            {turnaround && (
              <TargetClock
                style={turnaround.overdue ? { color: SCORE_LOW } : undefined}
              >
                <ScheduleIcon style={{ fontSize: 16 }} />
                {turnaround.label}
              </TargetClock>
            )}
            {locked || isOwnStudy ? (
              <BackToQueueButton
                variant="outlined"
                onClick={() => navigate(linkToEpocusReview())}
              >
                Back to queue
              </BackToQueueButton>
            ) : (
              <RoleGate can="submitReview">
                <Box display="flex" style={{ gap: 8 }}>
                  <SaveDraftButton
                    variant="outlined"
                    disabled={!canWriteReview}
                    onClick={handleSave}
                  >
                    {saving ? (
                      <CircularProgress
                        size={22}
                        style={{ color: EPOCUS_PRIMARY }}
                      />
                    ) : (
                      "Save"
                    )}
                  </SaveDraftButton>
                  <SubmitButton
                    variant="contained"
                    disableElevation
                    disabled={!canWriteReview}
                    onClick={handleSubmit}
                  >
                    {submitting ? (
                      <CircularProgress
                        size={22}
                        style={{ color: EPOCUS_WHITE }}
                      />
                    ) : (
                      "Submit review"
                    )}
                  </SubmitButton>
                </Box>
              </RoleGate>
            )}
          </WorkflowFooter>
        </WorkflowColumn>
      </SplitLayout>
    </PageRoot>
  );
};
